import { get } from 'svelte/store';
//Helpers
import { postReq } from 'src/services/http';
import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { createVirtualDevice, deleteVirtualDevice } from '../virtual_components_helpers';
import { saveVCSettings } from '../virtual_components_settings_helpers';
//DUI
import { device_ui } from 'src/stores/features/device-ui';
import { DeviceUIField } from 'src/types/DeviceUI';
import { duiDelete, duiSave, duiSaveField } from 'src/services/php-api/device_ui';
//Types
import type { Device } from 'src/types/Device';
import type { CloudApiResponse } from 'src/types/responses/CloudApiResponse';
import type { VirtualDeviceTemplate } from './virtual_template_types';
import type { VirtualComponentInterface } from 'src/types/VirtualComponents';

/**
 * Create a new virtual template on a device.
 * If the groupComponent is null, it will create a new virtual group component
 * and then create the virtual template on it.
 * @param {Device} parentDevice The device to create the virtual template on.
 * @param {string} groupComponent The group component to create the virtual template on.
 * @param {VirtualDeviceTemplate} [virtualTemplate] The virtual template to create.
 * @returns {Promise<CloudApiResponse>} A promise that resolves with the result of the request.
 */
export const createVirtualTemplate = async (
	parentDevice: Device,
	groupComponent: string | null,
	virtualTemplate: VirtualDeviceTemplate,
	groupSettings: VirtualComponentInterface['config'],
): Promise<CloudApiResponse | null> => {
	let res: CloudApiResponse | null = null;
	if (groupComponent == null) {
		const type = 'group';
		res = await gen2Request(parentDevice, 'Virtual.Add', {
			type,
			config: groupSettings,
		});
		if (res?.isok && res?.data?.id != null) {
			groupComponent = `group:${res.data.id}`;
		} else return res;
	}
	res = await createVirtualDevice(parentDevice, groupSettings, groupComponent);
	const deviceID = res?.isok && res?.data?.device_id;
	if (deviceID) {
		res = await duiSave(deviceID, { [`${DeviceUIField.VC_TEMPLATE}`]: virtualTemplate });
		res = await updateGroupComponents(res, parentDevice, groupComponent, virtualTemplate);
	}
	return res;
};

export const deleteVirtualTemplate = async (
	virtualDevice: Device,
	parentDevice: Device,
	deleteGroup: boolean,
): Promise<CloudApiResponse> => {
	await duiDelete(virtualDevice.id);
	let res: CloudApiResponse = await deleteVirtualDevice(virtualDevice);
	if (res?.isok && deleteGroup) {
		res = await gen2Request(parentDevice, 'Virtual.Delete', {
			key: `group:${virtualDevice?.channel}`,
		});
	}
	return res;
};

/**
 * Updates the virtual template of a device and its group components.
 * @param {Device} virtualDevice The device to update the virtual template on.
 * @param {Device} parentDevice The device to update the group components on.
 * @param {string} groupComponent The group component to save the group components on.
 * @param {VirtualDeviceTemplate} [virtualTemplate] The virtual template to update or null to delete.
 * @returns {Promise<CloudApiResponse> | null} A promise that resolves with the result of the request or null if no changes were made.
 * @throws If the virtual template is invalid.
 */
export const updateVirtualTemplate = async (
	virtualDevice: Device,
	parentDevice: Device,
	groupComponent: string,
	virtualTemplate: VirtualDeviceTemplate,
	groupSettings: VirtualComponentInterface['config'],
): Promise<CloudApiResponse> => {
	let res: CloudApiResponse | null = await duiSaveField(
		virtualDevice.id,
		DeviceUIField.VC_TEMPLATE,
		virtualTemplate,
	);
	res = await updateGroupComponents(res, parentDevice, groupComponent, virtualTemplate);
	if ((res?.isok && groupSettings?.name) !== virtualDevice.name) {
		virtualDevice.name = groupSettings?.name || virtualDevice.name;
		res = await saveVCSettings(parentDevice, 'group', groupSettings);
		res = await postReq(`/interface/device/save`, {
			id: virtualDevice.id,
			data: JSON.stringify(virtualDevice),
		});
		if (res?.isok) {
			devices.update(virtualDevice);
		}
	}
	return res;
};

/**
 * Update the group components of a device if the virtual template has changed.
 * It will update the device's virtual template and then save the group components.
 * @param {Device} parentDevice The device to update the group components on.
 * @param {string} groupComponent The group component to save the group components on.
 * @param {VirtualDeviceTemplate} [virtualTemplate] The virtual template to update.
 * @returns {Promise<CloudApiResponse> | null} A promise that resolves with the result of the request or null if no changes were made.
 * @throws If the virtual template is invalid.
 */
const updateGroupComponents = async (
	res: CloudApiResponse,
	parentDevice: Device,
	groupComponent: string,
	virtualTemplate?: VirtualDeviceTemplate,
): Promise<CloudApiResponse | null> => {
	if (!res?.isok) return res;
	const deviceStatus = devices_status.getByRealId(parentDevice.id);
	const groupComponents = deviceStatus?.[groupComponent]?.value || [];
	const selectedItems = Object.values(virtualTemplate || {}).filter((i) => /^(\w+):2\d{2}$/.test(i));
	const comparer = (arr: string[]) => [...arr].sort().join(',');
	if (groupComponent && comparer(groupComponents) !== comparer(selectedItems)) {
		const groupID = Number(groupComponent.split(':')?.[1]);
		res = await saveGroupComponents(parentDevice, groupID, selectedItems);
	}
	return res;
};

/**
 * Save the group components on a device.
 * It will send a request to the Gen 2 server to save the group components.
 * @param {Device} parentDevice The device to save the group components on.
 * @param {number} groupID The ID of the group to save the group components on.
 * @param {string[]} selectedItems The virtual components to save in the group.
 * @returns {Promise<CloudApiResponse>} A promise that resolves with the result of the request.
 */
export const saveGroupComponents = async (
	parentDevice: Device,
	groupID: number,
	selectedItems: string[],
): Promise<CloudApiResponse> => {
	const res: CloudApiResponse = await gen2Request(parentDevice, 'Group.Set', {
		id: groupID,
		value: selectedItems,
	});
	return res;
};

/**
 * Get the virtual template of a device.
 * It will return the virtual template if the device has one, otherwise it will return null.
 * @param {Device} virtualDevice The device to get the virtual template from.
 * @returns {VirtualDeviceTemplate | null} The virtual template of the device or null if it does not have one.
 */
export const getVirtualTemplate = (virtualDevice: Device): VirtualDeviceTemplate | null => {
	return get(device_ui)?.[virtualDevice?.id]?.vc_template || null;
};
